import { css } from '@emotion/react'
import { Alert, Cell, FormControl, Grid, Heading, Tooltip, VFlow } from 'bold-ui'
import { CheckboxField, NumberField } from 'components/form'
import { GrauParentescoSelectField } from 'components/form/field/select/GrauParentescoSelectField/GrauParentescoSelectField'
import { ResponsavelFamiliarSelectField } from 'components/form/field/select/ResponsavelFamiliarSelectField/ResponsavelFamiliarSelectField'
import { LotacaoResponsavelSection } from 'components/lotacao-responsavel/LotacaoResponsavelSection'
import { TourTarget } from 'components/tour/components/TourTarget'
import { MetaPath } from 'util/metaPath'
import { MSG_RESPONSAVEL_PRECISA_DE_CPF_OU_CNS } from 'view/cidadao/constants-cidadao'

import { CidadaoTourIdEnum } from '../tour-cidadao'
import { ResponsabilidadeAcompanhamentoFormModel } from '../types/model-cidadao'

interface ResponsabilidadeAcompanhamentoFormSectionProps {
  meta: MetaPath<ResponsabilidadeAcompanhamentoFormModel>
  values: ResponsabilidadeAcompanhamentoFormModel
  cidadaoFaleceu: boolean
  isCidadaoAldeado: boolean
  cpfCidadao: string
  cnsCidadao: string
  isLotacaoActive?: boolean
  isModoSimplificadoForaAps?: boolean
  isCadIndividual?: boolean
  apenasAps?: boolean
}

export const ResponsabilidadeAcompanhamentoFormSection = ({
  meta,
  values,
  cidadaoFaleceu,
  isCidadaoAldeado,
  cpfCidadao,
  cnsCidadao,
  isLotacaoActive,
  isModoSimplificadoForaAps = false,
  isCadIndividual = false,
  apenasAps = false,
}: ResponsabilidadeAcompanhamentoFormSectionProps) => {
  if (isCadIndividual && isModoSimplificadoForaAps) {
    return null
  }

  const hasNoCpfOrCns = !cpfCidadao && !cnsCidadao

  return (
    <Grid>
      <Cell size={7}>
        <TourTarget id={CidadaoTourIdEnum.FORM_EQUIPE_REFERENCIA}>
          <VFlow vSpacing={1}>
            <Heading level={2}>
              {isCadIndividual ? 'Equipe de referência' : 'Responsabilidade de Acompanhamento'}
            </Heading>

            <LotacaoResponsavelSection
              name={meta.responsavel}
              isCadastroIndividual
              hideProfissionalAndCboInCard={isCadIndividual}
              equipeLabel={isCadIndividual ? 'Equipe' : undefined}
              equipeFallbackLabel={isCadIndividual ? 'Sem equipe específica' : undefined}
              alterarResponsavelLabel={isCadIndividual ? 'Alterar equipe de referência' : undefined}
              modalTitle={isCadIndividual ? 'Alterar equipe de referência' : undefined}
              modalLotacaoLabel={isCadIndividual ? 'Busque por um profissional da equipe' : undefined}
              disableProfissionalFilters={isCadIndividual || undefined}
              apenasAps={apenasAps}
            />
          </VFlow>
        </TourTarget>
      </Cell>
      <Cell size={5} />
      <Cell size={12}>
        <TourTarget id={CidadaoTourIdEnum.FORM_TERRITORIO}>
          <Grid>
            {!isCadIndividual && (
              <Cell size={12}>
                {isLotacaoActive === false && (
                  <Alert type='danger' inline>
                    O responsável informado possui uma lotação inativa. Atualize para a nova lotação alterando o
                    responsável.
                  </Alert>
                )}
              </Cell>
            )}
            <Cell size={6}>
              <ResponsavelFamiliarSelectField
                name={meta.responsavelFamiliar}
                label={isCidadaoAldeado ? 'Chefe de família' : 'Responsável familiar'}
                disabled={values?.isResponsavelFamiliar}
              />
            </Cell>
            <Cell size={6}>
              <Tooltip text={hasNoCpfOrCns ? MSG_RESPONSAVEL_PRECISA_DE_CPF_OU_CNS : undefined}>
                <div style={{ display: 'inline-block' }}>
                  <FormControl label={<span>&nbsp;</span>}>
                    <CheckboxField
                      name={meta.isResponsavelFamiliar}
                      label={isCidadaoAldeado ? 'Cidadão é o chefe de família' : 'Cidadão é o responsável familiar'}
                      disabled={hasNoCpfOrCns && !values?.isResponsavelFamiliar}
                    />
                  </FormControl>
                </div>
              </Tooltip>
            </Cell>
            <Cell size={6}>
              <GrauParentescoSelectField
                name={meta.grauParentescoResponsavelFamiliar}
                label={
                  isCidadaoAldeado
                    ? 'Grau de parentesco com o chefe de família'
                    : 'Grau de parentesco com o responsável'
                }
                disabled={
                  values?.isResponsavelFamiliar ||
                  !(values?.responsavelFamiliar?.cpf || values?.responsavelFamiliar?.cns)
                }
              />
            </Cell>
            <Cell size={6} />
            <Cell size={3}>
              <NumberField name={meta.microarea} disabled={values?.statusForaArea} label='Microárea' maxLength={2} />
            </Cell>
            <Cell size={4}>
              <FormControl label={<span>&nbsp;</span>}>
                <CheckboxField name={meta.statusForaArea} label='Fora de área' />
              </FormControl>
            </Cell>
            <Cell size={5} />

            <Cell size={4}>
              <Tooltip
                text={cidadaoFaleceu ? 'Não é necessário remover do território um cidadão que já faleceu.' : ''}
                style={css`
                  margin-left: -4rem;
                `}
              >
                <CheckboxField
                  name={meta.statusCidadaoSaiuTerritorio}
                  label='Cidadão saiu do território'
                  disabled={cidadaoFaleceu}
                />
              </Tooltip>
            </Cell>
            <Cell size={8} />
          </Grid>
        </TourTarget>
      </Cell>
    </Grid>
  )
}
